import React, { useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useT } from "@/i18n";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter,
} from "@/components/ui/alert-dialog";

/**
 * Reusable confirmation dialog for destructive / important actions.
 * Replaces native window.confirm across the app.
 *
 * Props:
 * - open: boolean
 * - onOpenChange: (bool) => void
 * - title, description: strings (already localized by caller)
 * - resourceName: optional name of the affected resource (shown in bold)
 * - variant: "destructive" | "default"
 * - confirmLabel, cancelLabel: optional override labels
 * - onConfirm: async () => void   (dialog handles loading + errors)
 */
export default function ConfirmActionDialog({
  open, onOpenChange, title, description, resourceName,
  variant = "destructive", confirmLabel, cancelLabel, onConfirm,
}) {
  const t = useT();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isDestructive = variant === "destructive";

  const close = (v) => {
    if (loading) return;
    setError(null);
    onOpenChange(v);
  };

  const confirm = async () => {
    setError(null);
    setLoading(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || t("common.confirm.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={close}>
      <AlertDialogContent data-testid="confirm-action-dialog">
        <AlertDialogHeader>
          <div className="flex items-start gap-3">
            {isDestructive && (
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                <AlertTriangle className="h-5 w-5" />
              </span>
            )}
            <div className="min-w-0">
              <AlertDialogTitle className="font-display tracking-tight" data-testid="confirm-action-title">
                {title}
              </AlertDialogTitle>
              <AlertDialogDescription className="mt-1" data-testid="confirm-action-description">
                {description}
                {resourceName && (
                  <> <span className="font-semibold text-foreground">{resourceName}</span></>
                )}
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>

        {error && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="confirm-action-error">
            {error}
          </p>
        )}

        <AlertDialogFooter>
          <button
            type="button"
            onClick={() => close(false)}
            disabled={loading}
            data-testid="confirm-action-cancel"
            className="rounded-md border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-secondary disabled:opacity-60"
          >
            {cancelLabel || t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={loading}
            data-testid="confirm-action-confirm"
            className={`inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-transform hover:opacity-90 active:scale-95 disabled:opacity-60 ${
              isDestructive
                ? "bg-destructive text-destructive-foreground"
                : "bg-primary text-primary-foreground"
            }`}
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? t("common.confirm.processing") : (confirmLabel || t("common.confirm.confirm"))}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
